import { el, clear } from './dom.ts';
import { mountTurnstile, type TurnstileController } from './turnstile.ts';
import type { AccountManager } from '../auth/manager.ts';
import type { SleeperProfile } from '../auth/types.ts';

export function renderAccountPanel(
  host: HTMLElement,
  manager: AccountManager,
  onClose: () => void,
  turnstileSiteKey: string | null,
): void {
 let turnstile: TurnstileController | null = null;
 let captchaToken: string | null = null;
 let mergeDecisions = new Map<string, 'device' | 'account'>();
  let unsubscribe: (() => void) | null = null;
  const closePanel = () => {
    unsubscribe?.();
    unsubscribe = null;
    onClose();
  };

  const render = () => {
    clear(host);
    turnstile = null;
    const state = manager.getState();
    const modal = el('section', { class: 'modal account-modal', role: 'dialog', 'aria-modal': 'true', 'aria-label': 'Account' });
    modal.appendChild(el('button', { class: 'modal-close', type: 'button', onClick: onClose, 'aria-label': 'Close' }, '×'));
    modal.appendChild(el('h2', {}, 'Draft Puppy account'));

    if (state.auth.status === 'disabled') {
      modal.appendChild(el('p', { class: 'hint' }, 'Accounts are not enabled on this deployment yet. Guest mode is fully available.'));
      modal.appendChild(el('button', { class: 'btn', type: 'button', onClick: closePanel }, 'Continue as guest'));
    } else if (state.auth.status === 'loading') {
      modal.appendChild(el('p', { role: 'status' }, 'Checking account status…'));
    } else if (state.auth.status === 'signed-out') {
      renderSignedOut(modal);
    } else if (state.pending) {
      renderPending(modal, state.pending);
    } else {
      renderSignedIn(modal, state.auth.email, state.snapshot.profiles);
    }
    if (state.error) modal.appendChild(el('p', { class: 'account-error', role: 'alert' }, state.error));
   host.appendChild(modal);
    modal.querySelector<HTMLButtonElement>('.modal-close')?.addEventListener('click', closePanel, { once: true });
  };

  const renderSignedOut = (modal: HTMLElement) => {
    const email = el('input', {
      class: 'input',
      type: 'email',
      autocomplete: 'email',
      placeholder: 'you@example.com',
      required: true,
      'aria-label': 'Email address',
    }) as HTMLInputElement;
    const status = el('p', { class: 'account-status', role: 'status', 'aria-live': 'polite' });
    const captchaHost = el('div', { class: 'account-captcha' });
    const submit = el('button', { class: 'btn btn-primary', type: 'submit' }, 'Email me a sign-in link') as HTMLButtonElement;
    const form = el(
      'form',
      {
        class: 'account-form',
        onSubmit: async (event: Event) => {
          event.preventDefault();
          submit.disabled = true;
          status.textContent = 'Sending a secure sign-in link…';
          try {
            await manager.sendMagicLink(email.value, captchaToken ?? turnstile?.getToken() ?? null);
            status.textContent = 'If that email can receive messages, a sign-in link is on its way.';
          } catch (error) {
            submit.disabled = false;
            status.textContent = error instanceof Error ? error.message : 'Could not send the sign-in link.';
            turnstile?.reset();
          }
        },
      },
      el('label', { for: 'account-email' }, 'Email address'),
      email,
      captchaHost,
      submit,
      status,
    );
    email.id = 'account-email';
    modal.appendChild(el('p', { class: 'hint' }, 'No password. We will email you a one-time link. Your Sleeper data stays separate from guest mode.'));
    modal.appendChild(form);
    modal.appendChild(
      el(
        'p',
        { class: 'hint account-legal' },
        'By continuing, you agree to the ',
        el('a', { href: 'https://github.com/FearNoEvil97/Draft-Puppy/blob/main/docs/PRIVACY.md', target: '_blank', rel: 'noopener noreferrer' }, 'Privacy policy'),
        ' and ',
        el('a', { href: 'https://github.com/FearNoEvil97/Draft-Puppy/blob/main/docs/TERMS.md', target: '_blank', rel: 'noopener noreferrer' }, 'Terms of use'),
        '.',
      ),
    );
    if (turnstileSiteKey) void mountTurnstile(captchaHost, turnstileSiteKey, (token) => { captchaToken = token; }).then((controller) => { turnstile = controller; }).catch(() => {
      status.textContent = 'Verification is unavailable. Please try again later.';
    });
  };

  const renderPending = (modal: HTMLElement, pending: NonNullable<ReturnType<AccountManager['getState']>['pending']>) => {
    modal.appendChild(
      el(
        'p',
        {},
        pending.reason === 'conflict'
          ? 'This account changed on another device. Choose which saved version to keep before syncing again.'
          : 'This device and your account both have saved data. Choose what to keep before syncing.',
      ),
    );
    modal.appendChild(
      el('div', { class: 'account-choice-list' },
        choiceButton('Use this device', 'Upload this device’s guest profiles and rankings.', () => attempt(() => manager.commitInitialSync('device'))),
        choiceButton('Use saved account', 'Load the account and leave this device’s guest data untouched.', () => attempt(() => manager.commitInitialSync('account'))),
      ),
    );

    const merge = el('div', { class: 'account-merge' });
    merge.appendChild(el('h3', {}, 'Merge without losing conflicts'));
    if (pending.conflicts.length === 0) {
      merge.appendChild(el('p', { class: 'hint' }, 'There are no collisions. Both sets will be kept.'));
    } else {
      for (const conflict of pending.conflicts) {
        const key = `${conflict.kind}:${conflict.key}`;
        const row = el('div', { class: 'account-conflict' });
        row.appendChild(el('span', {}, `${conflict.kind === 'profile' ? 'Sleeper profile' : 'Ranking source'}: `));
        const device = el('button', { class: `chip ${mergeDecisions.get(key) === 'device' ? 'active' : ''}`, type: 'button', onClick: () => { mergeDecisions.set(key, 'device'); render(); } }, `This device — ${conflict.deviceLabel}`);
        const account = el('button', { class: `chip ${mergeDecisions.get(key) === 'account' ? 'active' : ''}`, type: 'button', onClick: () => { mergeDecisions.set(key, 'account'); render(); } }, `Saved account — ${conflict.accountLabel}`);
        row.append(device, account);
        merge.appendChild(row);
      }
    }
    const complete = pending.conflicts.every((conflict) => mergeDecisions.has(`${conflict.kind}:${conflict.key}`));
    merge.appendChild(el('button', { class: 'btn btn-primary', type: 'button', disabled: !complete, onClick: () => attempt(() => manager.commitInitialSync('merge', mergeDecisions)) }, 'Merge and sync'));
    modal.appendChild(merge);
  };

  const renderSignedIn = (modal: HTMLElement, email: string | null, profiles: SleeperProfile[]) => {
    modal.appendChild(el('p', { class: 'account-email' }, email ?? 'Signed in'));
    modal.appendChild(el('p', { class: 'hint' }, 'Your saved setup is scoped to each remembered Sleeper username.'));
    const list = el('div', { class: 'account-profiles' });
    if (profiles.length === 0) list.appendChild(el('p', { class: 'hint' }, 'No Sleeper profiles saved yet. Start a draft to save one.'));
    for (const profile of profiles) {
      list.appendChild(el('button', { class: 'choice', type: 'button', onClick: () => { manager.selectProfile(profile); closePanel(); } }, el('span', { class: 'choice-title' }, profile.sleeper_username), el('span', { class: 'choice-sub' }, 'Use this saved setup')));
    }
    modal.appendChild(list);
    modal.appendChild(el('div', { class: 'account-actions' },
      el('button', { class: 'btn', type: 'button', onClick: () => attempt(() => manager.signOut()) }, 'Sign out'),
      el('button', { class: 'btn btn-danger', type: 'button', onClick: () => { if (confirm('Delete your account and all synchronized data?')) attempt(() => manager.deleteAccount()); } }, 'Delete account'),
    ));
  };

  const choiceButton = (title: string, description: string, onClick: () => void): HTMLElement =>
    el('button', { class: 'choice', type: 'button', onClick }, el('span', { class: 'choice-title' }, title), el('span', { class: 'choice-sub' }, description));

 render();
  unsubscribe = manager.subscribe(render);

  function attempt(action: () => Promise<void>): void {
    void action().catch(() => {
      // The manager publishes the user-facing error and retains the local copy.
    });
  }
}
